import React, { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link as RouterLink } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCookieConsent } from '../../hooks/useCookieConsent';
import { cn } from '../../lib/utils';

const actionButtonClass =
  'min-h-7 touch-manipulation whitespace-nowrap rounded-[9px] px-2 text-center text-[10.5px] font-semibold leading-tight transition-[background-color,color,border-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rely-color-brand-green)] focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-neutral-950 sm:min-h-9 sm:rounded-lg sm:px-2.5 sm:text-xs';

const APP_COOKIE_SLOT_ID = 'rely-cookie-banner-slot';

const getAppCookieSlot = () => {
  if (typeof document === 'undefined') return null;
  return document.getElementById(APP_COOKIE_SLOT_ID);
};

const CookieBanner = () => {
  const { t } = useTranslation();
  const { showBanner, acceptAll, acceptEssential, rejectAll, isLoading } =
    useCookieConsent();
  const [appSlot, setAppSlot] = useState(getAppCookieSlot);

  useLayoutEffect(() => {
    setAppSlot(getAppCookieSlot());
  }, []);

  // Don't show while loading or if consent already given
  if (isLoading || !showBanner) {
    return null;
  }

  const title = t('cookies.banner.title', 'Cookies');
  const description = t(
    'cookies.banner.description',
    'Esenciales y opcionales ayudan a mejorar Rely.'
  );
  const learnMore = t(
    'cookies.banner.learnMore',
    'Política de cookies'
  );

  const isAppFlow = Boolean(appSlot);
  const banner = (
    <section
      data-testid="cookie-banner"
      data-layout="dock-aware"
      data-mobile-dock-clearance="compact"
      data-mobile-density="compact"
      data-placement={isAppFlow ? 'app-flow' : 'floating'}
      data-rely-surface="consent"
      aria-label={title}
      className={cn(
        'border border-neutral-200/90 bg-white/95 text-neutral-950 backdrop-blur-xl',
        'dark:border-neutral-800/90 dark:bg-neutral-950/95 dark:text-white dark:shadow-[0_24px_80px_rgba(0,0,0,0.48)]',
        isAppFlow
          ? [
              'fixed left-1/2 right-auto bottom-[calc(var(--rely-mobile-dock-height)+var(--rely-mobile-dock-edge)+0.875rem)] z-50 w-[min(calc(100vw-1rem),23rem)] -translate-x-1/2 rounded-[16px] p-2 shadow-[0_18px_64px_rgba(33,33,33,0.16)] sm:p-4',
              'md:relative md:bottom-auto md:left-auto md:right-auto md:z-auto md:mb-0 md:ml-auto md:mt-5 md:w-[min(44rem,100%)] md:translate-x-0 md:rounded-[var(--rely-radius-panel)] md:p-4 md:shadow-[0_18px_64px_rgba(33,33,33,0.16)]',
            ]
          : [
              'fixed left-1/2 right-auto bottom-[calc(var(--rely-mobile-dock-height)+var(--rely-mobile-dock-edge)+0.875rem)] z-50 w-[min(calc(100vw-1rem),23rem)] -translate-x-1/2 rounded-[16px] p-2 shadow-[0_18px_64px_rgba(33,33,33,0.16)] sm:p-4',
              'md:bottom-4 md:w-[min(44rem,calc(100vw-2rem))] md:rounded-[var(--rely-radius-panel)]',
            ]
      )}
    >
      <div className="grid min-w-0 grid-cols-1 gap-1.5 md:flex md:items-center md:justify-between md:gap-4">
        <div className="flex min-w-0 gap-3">
          <span
            data-testid="cookie-banner-icon-frame"
            className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-xl bg-[var(--rely-color-brand-green)]/15 text-[var(--rely-color-brand-black)] ring-1 ring-[var(--rely-color-brand-green)]/35 dark:bg-[var(--rely-color-brand-green)]/15 dark:text-[var(--rely-color-brand-green)]"
          >
            <Cookie
              className="h-4 w-4"
              strokeWidth={2.2}
              aria-hidden="true"
            />
          </span>

          <div className="min-w-0">
            <p
              data-testid="cookie-banner-copy"
              className="line-clamp-2 text-[10.5px] leading-4 text-neutral-600 dark:text-neutral-400 sm:text-sm sm:leading-5"
            >
              <span className="font-semibold text-neutral-950 dark:text-white">
                {title}
              </span>
              <span aria-hidden="true">.</span>{' '}
              {description}{' '}
              <RouterLink
                to="/cookies"
                className="font-semibold text-neutral-950 underline decoration-[var(--rely-color-brand-green)] decoration-2 underline-offset-4 transition-colors duration-200 hover:text-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--rely-color-brand-green)] dark:text-white dark:hover:text-[var(--rely-color-brand-green)]"
              >
                {learnMore}
              </RouterLink>
            </p>
          </div>
        </div>

        <div
          data-testid="cookie-banner-actions"
          className="flex min-w-0 items-center justify-end gap-1 overflow-x-auto md:flex-none md:gap-2"
        >
          <button
            type="button"
            onClick={rejectAll}
            className={cn(
              actionButtonClass,
              'px-2 text-neutral-600 hover:bg-neutral-100 hover:text-neutral-950 dark:text-neutral-400 dark:hover:bg-neutral-900 dark:hover:text-white'
            )}
          >
            {t('cookies.banner.reject', 'Rechazar')}
          </button>
          <button
            type="button"
            onClick={acceptEssential}
            className={cn(
              actionButtonClass,
              'border border-neutral-200 bg-white text-neutral-800 hover:border-neutral-300 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-200 dark:hover:border-neutral-700 dark:hover:bg-neutral-900'
            )}
          >
            {t('cookies.banner.essentialOnly', 'Solo esenciales')}
          </button>
          <button
            type="button"
            onClick={acceptAll}
            className={cn(
              actionButtonClass,
              'bg-[var(--rely-color-brand-green)] text-[var(--rely-color-brand-black)] shadow-[0_10px_28px_rgba(113,222,128,0.24)] hover:bg-primary-600'
            )}
          >
            {t('cookies.banner.acceptAll', 'Aceptar todo')}
          </button>
        </div>
      </div>
    </section>
  );

  return isAppFlow ? createPortal(banner, appSlot) : banner;
};

export default CookieBanner;
